import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal, shipping, tax, total } = useCart();
  const { isPrelaunch } = useSiteSettings();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [addr, setAddr] = useState({
    full_name: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US", phone: "",
  });

  const set = (k, v) => setAddr((a) => ({ ...a, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (isPrelaunch) {
      toast.error("Online ordering opens soon. Contact sales@interstate-pc.com.");
      return;
    }
    if (items.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        shipping: addr,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return <div className="container-tight py-16 text-center text-zinc-500">Your cart is empty. <button onClick={() => nav('/catalog')} className="text-[#FF5C00] underline">Browse products</button></div>;
  }

  return (
    <div className="container-tight py-8" data-testid="checkout-page">
      <div className="mb-6">
        <div className="font-mono text-xs tracking-widest text-[#FF5C00] mb-1">// CHECKOUT</div>
        <h1 className="font-display font-black text-3xl md:text-4xl">SHIPPING & PAYMENT</h1>
      </div>

      <div className="grid lg:grid-cols-[1fr_400px] gap-8">
        <form onSubmit={submit} className="space-y-6" data-testid="shipping-form">
          <section className="border border-zinc-800 bg-[#0a0a0c] p-6 rounded-sm">
            <div className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-4">Ship-to Address</div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Full Name" required value={addr.full_name} onChange={(v) => set("full_name", v)} testId="ship-name" className="col-span-2" />
              <Field label="Address Line 1" required value={addr.line1} onChange={(v) => set("line1", v)} testId="ship-line1" className="col-span-2" />
              <Field label="Address Line 2 (optional)" value={addr.line2} onChange={(v) => set("line2", v)} testId="ship-line2" className="col-span-2" />
              <Field label="City" required value={addr.city} onChange={(v) => set("city", v)} testId="ship-city" />
              <Field label="State" required value={addr.state} onChange={(v) => set("state", v)} testId="ship-state" />
              <Field label="ZIP" required value={addr.postal_code} onChange={(v) => set("postal_code", v)} testId="ship-zip" />
              <Field label="Phone" value={addr.phone} onChange={(v) => set("phone", v)} testId="ship-phone" />
            </div>
          </section>
          <section className="border border-zinc-800 bg-[#0a0a0c] p-6 rounded-sm">
            <div className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-2">Payment</div>
            {isPrelaunch ? (
              <p className="text-sm text-yellow-300/90">Online ordering opens soon. No payment will be taken during pre-launch. For inquiries email <a href="mailto:sales@interstate-pc.com" className="underline">sales@interstate-pc.com</a>.</p>
            ) : (
              <p className="text-sm text-zinc-400">You'll be redirected to Stripe's secure checkout to complete payment. Test card: <span className="font-mono text-[#00F0FF]">4242 4242 4242 4242</span> · any future date · any CVC.</p>
            )}
          </section>
          <Button data-testid="pay-btn" type="submit" disabled={loading || isPrelaunch}
            className="w-full h-12 bg-[#FF5C00] hover:bg-[#FF7A33] rounded-sm font-mono tracking-wider glow-orange disabled:opacity-40">
            {isPrelaunch ? "ONLINE ORDERING OPENS SOON" : (loading ? "REDIRECTING..." : `PAY ${formatPrice(total)}`)}
          </Button>
        </form>

        <aside className="border border-zinc-800 bg-[#0a0a0c] p-6 rounded-sm h-fit sticky top-24">
          <div className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-4">Order Summary</div>
          <div className="space-y-2 max-h-60 overflow-y-auto pr-1 mb-4">
            {items.map((i) => (
              <div key={i.product_id} className="flex gap-2 text-xs">
                <img src={i.image} alt="" className="w-10 h-10 object-cover border border-zinc-800" />
                <div className="flex-1 min-w-0">
                  <div className="truncate text-zinc-300">{i.name}</div>
                  <div className="font-mono text-zinc-500">×{i.quantity}</div>
                </div>
                <div className="font-mono text-white">{formatPrice(i.price * i.quantity)}</div>
              </div>
            ))}
          </div>
          <div className="space-y-1 text-sm border-t border-zinc-800 pt-3">
            <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span className="font-mono text-white">{formatPrice(subtotal)}</span></div>
            <div className="flex justify-between text-zinc-400"><span>Shipping</span><span className="font-mono text-white">{formatPrice(shipping)}</span></div>
            <div className="flex justify-between text-zinc-400"><span>Tax</span><span className="font-mono text-white">{formatPrice(tax)}</span></div>
            <div className="flex justify-between pt-2 border-t border-zinc-800 mt-2">
              <span className="font-mono text-xs uppercase tracking-widest">Total</span>
              <span className="font-mono font-bold text-lg">{formatPrice(total)}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required, testId, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs font-mono tracking-widest text-zinc-400 uppercase">{label}</Label>
      <Input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} required={required}
        className="bg-[#050505] border-zinc-800 rounded-sm mt-1 focus-visible:ring-[#FF5C00]" />
    </div>
  );
}
